import { useEffect, useState } from 'react';
import Page from '../../../components/page/Page';
import { Paper } from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import Header from '../../../components/queries/articles/Header';
import { useBusinessCore } from '../../../../core/modules/business';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useArticlesCore } from '../../../../core/modules/articles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { reclassifyApiService } from '../../../../core/modules/reclassify/services/api';



const Articles = () => {
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { articles, classifiedArticles, clearArticles, errorCode } = useArticlesCore();
  const { goToWelcome } = useNavigationCore();
  const { getBusinesses } = useBusinessCore();
  const [openTraceModal, setOpenTraceModal] = useState(false);
  const [traceRows, setTraceRows] = useState<any[]>([]);

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (
      !user || !user.privileges.superAdmin
    ) {
      goToWelcome();
    }
    clearArticles();
  }, [user, goToWelcome, clearArticles]);

  useEffect(() => {
    if (companyId) {
      getBusinesses(companyId);
    }
  }, [companyId, getBusinesses]);



  const handleOpenTrace = async (article: any) => {
    if (!companyId || !article.id) return;
    setTraceRows([]);
    setOpenTraceModal(true);
    try {
      const movements = await reclassifyApiService.getProductAuditReport(companyId, article.id);
      setTraceRows(
        movements.map((m: any) => {
          return {
            containerCode: m.container_id || '',
            location: m.location_id || '',
            user: m.user_name || '',
            date: new Date(m.date).toLocaleString('es-CL'),
          };
        })
      );
    } catch (e) {
      setTraceRows([]);
    }
  };

  const handleCloseTrace = () => setOpenTraceModal(false);

  const columns: GridColDef[] = [
    {
      field: 'trace',
      headerName: '',
      minWidth: 50,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton onClick={() => handleOpenTrace(params.row)} size="small">
          <VisibilityIcon />
        </IconButton>
      ),
    },
    {
      field: 'code',
      headerName: 'SKU',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'description',
      headerName: 'Descripción',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'category_name',
      headerName: 'Categoría',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'price',
      headerName: 'Precio',
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'quantity',
      headerName: 'Cant.',
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'location',
      headerName: 'Ubicación',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <div className="text-semibold">{params.row.location_name}</div>
          <div className="text-gray-400">{params.row.location_description}</div>
        </div>
      )
    },
    {
      field: 'warehouse',
      headerName: 'Bodega',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <div className="text-semibold">{params.row.warehouse_name}</div>
          <div className="text-gray-400">{params.row.warehouse_description}</div>
        </div>
      )
    },
    {
      field: 'status_name',
      headerName: 'Estado',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'provider_name',
      headerName: 'Proveedor',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
    },
    {
      field: 'container',
      headerName: 'Contenedor',
      minWidth: 200,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <div className="text-semibold">{params.row.container_code}</div>
          <div className="text-gray-400">{params.row.container_description}</div>
        </div>
      )
    },
    {
      field: 'reference',
      headerName: 'Referencia',
      cellClassName: 'flex items-center p-8',
    },
  ];

  // Determinar qué array mostrar
  const showArticles = articles.length > 0 ? articles : classifiedArticles;

  return (
    <>
      <Page
        header={<Header />}
        content={
          showArticles.length === 0 ? (
            <div className="w-full my-16 px-24 md:px-32">
              <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
                <div className="w-full p-32 text-center">
                  <span style={{ color: '#1976d2', fontWeight: 600, fontSize: 20, letterSpacing: 1 }}>
                    No hay artículos para mostrar
                  </span>
                </div>
              </Paper>
            </div>
          ) : (
            <div className="w-full my-16 px-24 md:px-32">
              <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
                <div className="w-full p-32">
                  <DataGrid
                    rows={showArticles}
                    columns={columns}
                    sx={{ border: 0 }}
                    disableColumnSorting
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    getRowHeight={() => 'auto'}
                    pagination={undefined}
                  />
                </div>
              </Paper>
            </div>
          )
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <Dialog open={openTraceModal} onClose={handleCloseTrace} maxWidth="md" fullWidth>
        <DialogTitle>Trazabilidad del Artículo</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><b>CÓDIGO DE CONTENEDOR</b></TableCell>
                  <TableCell><b>UBICACIÓN A LA QUE SE MOVIÓ</b></TableCell>
                  <TableCell><b>USUARIO</b></TableCell>
                  <TableCell><b>FECHA</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {traceRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No hay movimientos de auditoría para este artículo
                    </TableCell>
                  </TableRow>
                ) : (
                  traceRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.containerCode}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>{row.user}</TableCell>
                      <TableCell>{row.date}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Articles;
